import React from "react";

interface Props {
  children: React.ReactNode;
  /** Shown in the console log line so a report names the page that broke. */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time errors so one bad lookup can't white-screen the whole app.
 *
 * Born 2026-07-20 (ClickUp 86bb0gg67): a stored result carrying a sub-personality
 * name that no longer exists in SPECIFIC_CATEGORY_SECTIONS threw inside MainResults,
 * React unmounted the entire tree, and the user got a COMPLETELY blank page — no
 * message, no retry, `document.body.innerText` empty. That is the same symptom class
 * McKayla reported in June ("results page keeps spinning / won't load").
 *
 * This is the app-wide net, not the fix for any one lookup. Lookups should still fall
 * back gracefully (see MainResults) — the boundary exists for the case nobody predicted.
 *
 * It deliberately does NOT swallow the error: it re-logs to the console so the real
 * stack is still there for whoever is debugging, and so Sentry-style tooling can see it
 * if it is ever added.
 */
export default class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Surface it — never silently swallow. This is the only record of what broke.
    console.error(
      `[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ""}] render failed:`,
      error,
      info.componentStack
    );
  }

  private handleRetry = () => {
    this.setState({ error: null });
  };

  private handleRestart = () => {
    // The usual cause is a stale/incompatible stored result. Clearing it and going
    // back to the quiz is the one recovery a user can actually perform themselves.
    try {
      localStorage.removeItem("mpay_resultsData");
    } catch (e) {
      console.error("[ErrorBoundary] could not clear stored results:", e);
    }
    window.location.assign("/quiz");
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: 32,
          fontFamily: "Nunito, sans-serif",
        }}
      >
        <h1 style={{ fontSize: 32, fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
          Something went wrong on this page
        </h1>
        <p
          style={{
            fontSize: 18,
            color: "#555",
            maxWidth: 560,
            lineHeight: 1.5,
            marginTop: 12,
          }}
        >
          Sorry about that. Your results may have been saved in an older format that we
          can no longer read. You can try again, or retake the quiz to get a fresh set of
          results.
        </p>
        <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap", justifyContent: "center" }}>
          <button
            onClick={this.handleRetry}
            style={{
              backgroundColor: "#00A4EF",
              color: "#fff",
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "Nunito, sans-serif",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              cursor: "pointer",
            }}
          >
            Try again
          </button>
          <button
            onClick={this.handleRestart}
            style={{
              backgroundColor: "#fff",
              color: "#1a1a1a",
              fontSize: 16,
              fontWeight: 600,
              fontFamily: "Nunito, sans-serif",
              border: "2px solid #1a1a1a",
              borderRadius: 8,
              padding: "12px 24px",
              cursor: "pointer",
            }}
          >
            Retake the quiz
          </button>
        </div>
      </div>
    );
  }
}
